<template>
  <vx-card class="mt-base" :title="activityTitle">
    <h4 class="mb-5">All About Gears</h4>
    <p class="mb-5">
      The wheel was one of the first inventions of mechanical engineering. After its invention,
      engineers discovered that if they put ‘teeth’ on the wheel, they could use it to perform lots
      of different functions.
    </p>
    <p class="mb-5">These wheels with teeth are called gears.</p>
    <p class="mb-5">
      Watch the following video to learn more about gears and gear-related things. Don’t worry about
      the video quality too much. It might look like it was produced shortly after the first wheel
      was, but the content is excellent and still relevant today.
    </p>
    <p class="mb-5">
      In it, you learn about gear speed and direction, circumference, diameter, radius, combining
      different size gears, driver gear vs driven gear, gear trains, and gear ratio.
    </p>
    <responsive-youtube-wrapper :video="videos[1].video" :image="videos[1].image" />

    <h4 class="mb-5">What Are Gears?</h4>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/engineering/what_are_gears.png')"
      classes="2/3"
      alt="Slide: What are gears? Rotating wheel with teeth (cogs) that interlock with another wheel; used to transmit (and often multiply) rotational forces; hence they act in a way similar to levers. Photo of an old geared machine."
    />
    <h4 class="mb-5">Where Are Gears to Be Found?</h4>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/engineering/what_are_gears_found.png')"
      classes="2/3"
      alt="Slide: Where are gears found? Pretty much anything which moves, including cars (in the gearbox and many other places), bikes (usually attached to the chain) and children's toys."
    />

    <h4 class="mb-5">
      Question 1: Can you list 3 things in your house that you think have gears inside?
    </h4>
    <div class="overflow-hidden">
      <p class="float-left py-2 pr-3">1.</p>
      <text-block v-model="data.question1_1" :editable="isEditable" style="width: 300px" />
      <p class="float-left py-2 pr-2">2.</p>
      <text-block v-model="data.question1_2" :editable="isEditable" style="width: 300px" />
      <p class="float-left py-2 pr-2">3.</p>
      <text-block v-model="data.question1_3" :editable="isEditable" style="width: 300px" />
    </div>

    <h4 class="mb-5 mt-5">How Do Gears Work?</h4>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/engineering/how_do_they_work.png')"
      classes="2/3"
      alt="Slide: How do they work? Turning force applied to one wheel; interlocking wheel turns the adjacent wheel in the opposite direction; cogs prevent the wheels from slipping. Diagram of two interlocking gears."
    />
    <h4 class="mb-5 mt-5">Multiplying Force</h4>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/engineering/multiplying_force.png')"
      classes="2/3"
      alt="Slide: Multiplying force. A wheel with more teeth (which must be the same size!) turns slower; here, the bigger wheel turns at half the speed. Diagram of a 24-teeth gear meshing with a 12-teeth gear."
    />

    <centered-image-wrapper
      :image="require('@/assets/images/experiences/engineering/what_about_here.png')"
      classes="2/3"
      alt="Slide: What about here? The bigger wheel will turn a third of the speed of the smaller, but the moment of the turning force will be three times the size of the smaller wheel. Diagram of a 36-teeth gear meshing with a 12-teeth gear."
    />

    <div v-for="(question, index) in questions2" :key="'q2_' + index">
      <h5 class="mb-5 questionlabel">{{ question.question }}</h5>
      <radio-buttons-wrapper
        v-model="data['question_2_' + index]"
        :index="'q2'"
        :question="question"
        :is-editable="isEditable"
        show-question="false"
      />
    </div>

    <h4 class="mb-5">Parts of a Gear</h4>
    <p class="mb-5">Take a look at the parts of a gear in the diagram below.</p>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/engineering/teeth_circle.png')"
      classes="2/3"
      alt="Labelled diagram of a large gear meshing with a small gear. Labels: Teeth on the outer edge; Axis at the centre; Pitch Diameter drawn across the gear through the axis; Pitch Circle drawn through the teeth; Circular Pitch marked between two adjacent teeth."
    />
    <p class="mb-5">
      <strong>Axis: </strong>The axis of revolution of the gear, where the shaft passes through.
    </p>
    <p class="mb-5">
      <strong>Teeth: </strong>The jagged faces projecting outward from the circumference of the
      gear, used to transmit rotation to other gears.
    </p>
    <p class="mb-5">
      <strong>Pitch Circle: </strong>The circle that defines the "size" of the gear.
    </p>
    <p class="mb-5">
      <strong>Pitch Diameter: </strong> The pitch diameter refers to the working diameter of the
      gear, a.k.a., the diameter of the pitch circle.
    </p>
    <p class="mb-5">
      <strong>Circular Pitch: </strong>The distance from a point on one tooth to the same point on
      the adjacent tooth, measured along the pitch circle.
    </p>

    <h4 class="mb-5">
      Question 3: Can you correctly identify each part of the gear in the diagram below?
    </h4>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/engineering/act_5_question3.png')"
      classes="2/3"
      alt="Diagram of a large green gear meshing with a small blue gear, with five parts labelled a to e: a points to the gap between two adjacent teeth on the outer edge; b is a line drawn across the full width of the gear through its centre; c points to the yellow dot at the centre; d points to the red circle drawn through the teeth; e points to a single tooth."
    />

    <div v-for="(question, index) in questions3" :key="'3_' + index">
      <div class="overflow-hidden">
        <h5 class="mb-5 questionlabel float-left py-2 pr-2">{{ question.question }}</h5>
        <div class="vx-row" style="width: 300px">
          <div
            class="vx-col w-full mb-2"
            v-if="isEditable"
            :class="{
              wrong:
                data['question_3_' + index] > 0 && question.correct != data['question_3_' + index],
            }"
          >
            <select
              class="default-select"
              v-model="data['question_3_' + index]"
              :name="'question_3_' + index"
              :aria-label="question.question"
            >
              <option v-for="answer in question.answers" :key="answer.val" :value="answer.val">
                {{ answer.answer }}
              </option>
            </select>
          </div>
          <div class="my-2 mx-5" v-else>
            <div v-if="data['question_3_' + index] != 0 && data['question_3_' + index] != null">
              <p class="my-2" v-for="answer in question.answers" :key="answer.val">
                <span v-if="data['question_3_' + index] == answer.val">{{ answer.answer }}</span>
              </p>
            </div>
            <p class="my-2" v-else>N/A</p>
          </div>
        </div>
      </div>
    </div>

    <h4 class="mb-5 mt-5">Connect It - Gears in Action!</h4>
    <p class="mb-5">
      Do you remember in the video we learned that a driver gear turning in one direction will turn
      an adjacent gear in the opposite direction.
    </p>
    <p class="mb-5">
      This is straightforward enough when we are only dealing with two gears, such as in the diagram
      below:
    </p>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/engineering/teeth_direction.png')"
      classes="2/3"
      alt="A large blue gear with an arrow showing it turning clockwise, meshing with a small red gear with an arrow showing it turning anticlockwise."
    />
    <p class="mb-5">
      Notice how the blue gear is turning in a clockwise direction, while the red gear is turning in
      an anticlockwise direction.
    </p>
    <p class="mb-5">
      But things can get way more complicated when we are dealing with lots of different gears and
      driving gears of different sizes all working in different directions. It’s a lot to keep track
      of!
    </p>
    <p>
      Have a go at the following game to practise. In this game you’ll need to combine different
      gears to join the two wires. It starts off pretty easy, but as you go through the levels
      things get a lot more difficult - fast!
    </p>
    <p class="mb-5">
      Click the button on the left to play the game. The short video on the right gives you a few
      quick tips and trick on how to play the game.
    </p>
    <p class="mb-5">Good luck!</p>

    <div class="vx-row mb-5">
      <div class="vx-col lg:w-1/2">
        <button
          type="button"
          class="no-style img-click-button"
          aria-label="Open the Connect It gears game"
          @click="openGamePopup()"
        >
          <img
            :src="require('@/assets/images/experiences/engineering/game_splash_small.jpg')"
            class="center-item pt-10"
            alt=""
          />
        </button>
      </div>
      <div class="vx-col lg:w-1/2 flex justify-center">
        <responsive-iframe :src="videos[2].video" :posterimg="videos[2].image" />
      </div>
    </div>

    <p class="mb-5">How did you go? What level of the game did you get up to?</p>
    <div class="overflow-hidden">
      <p class="float-left py-2 pr-2">Enter the level you reached here:</p>
      <text-block v-model="data.question4" :editable="isEditable" style="width: 350px" />
    </div>

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
import RadioButtonsWrapper from '../../components/ActivitiesBlocks/RadioButtonsWrapper';
import store from '../../../../store/store';
import ResponsiveIframe from '@/components/UI/ResponsiveIframe';
export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/engineering/gears_splash.jpg'),
          video: 'https://www.youtube.com/embed/nYXCMs4a4BY',
        },
        2: {
          image: require('@/assets/images/experiences/engineering/how-to-play_game_splash.jpg'),
          video:
            $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
            'content/engineering/gear_game_instructions.mp4',
        },
      },

      question1_1: '',
      question1_2: '',
      question1_3: '',
      questions2: {
        1: {
          question:
            'Question 2: If you had a wheel with 36 teeth, what fraction of the speed of the 12 teeth wheel would it turn?',
          answers: [
            { val: 1, answer: 'a) One half', correct: false },
            { val: 2, answer: 'b) One quarter', correct: false },
            { val: 3, answer: 'c) One third', correct: true },
            { val: 4, answer: 'd) One twelfth', correct: false },
          ],
        },
      },
      answers: {
        question1: [],
        question2: [],
        question3: [],
        question4: [],
        question5: [],
      },
      correct_answers: {
        question1: true,
        question2: true,
        question3: true,
        question4: true,
        question5: true,
      },
      questions3: {
        1: {
          question: 'a = ',
          answers: [
            { val: undefined, answer: 'Please Select' },
            { val: 1, answer: 'Circular Pitch' },
            { val: 2, answer: 'Teeth' },
            { val: 3, answer: 'Axis' },
            { val: 4, answer: 'Pitch Diameter' },
            { val: 5, answer: 'Pitch Circle' },
          ],
          correct: 1,
        },
        2: {
          question: 'b = ',
          answers: [
            { val: undefined, answer: 'Please Select' },
            { val: 1, answer: 'Circular Pitch' },
            { val: 2, answer: 'Teeth' },
            { val: 3, answer: 'Axis' },
            { val: 4, answer: 'Pitch Diameter' },
            { val: 5, answer: 'Pitch Circle' },
          ],
          correct: 4,
        },
        3: {
          question: 'c = ',
          answers: [
            { val: undefined, answer: 'Please Select' },
            { val: 1, answer: 'Circular Pitch' },
            { val: 2, answer: 'Teeth' },
            { val: 3, answer: 'Axis' },
            { val: 4, answer: 'Pitch Diameter' },
            { val: 5, answer: 'Pitch Circle' },
          ],
          correct: 3,
        },
        4: {
          question: 'd = ',
          answers: [
            { val: undefined, answer: 'Please Select' },
            { val: 1, answer: 'Circular Pitch' },
            { val: 2, answer: 'Teeth' },
            { val: 3, answer: 'Axis' },
            { val: 4, answer: 'Pitch Diameter' },
            { val: 5, answer: 'Pitch Circle' },
          ],
          correct: 2,
        },
        5: {
          question: 'e = ',
          answers: [
            { val: undefined, answer: 'Please Select' },
            { val: 1, answer: 'Circular Pitch' },
            { val: 2, answer: 'Teeth' },
            { val: 3, answer: 'Axis' },
            { val: 4, answer: 'Pitch Diameter' },
            { val: 5, answer: 'Pitch Circle' },
          ],
          correct: 5,
        },
      },
    };
  },
  methods: {
    ...methods,
    openGamePopup() {
      store.dispatch(
        'popup/showIframePopup',
        'https://games.engineering.com/ConnectIT1.3/index.html',
      );
    },
  },
  computed: computed,
  inject: inject,
  components: { RadioButtonsWrapper, ResponsiveIframe },
};
</script>

<style scoped>
.wrong {
  background: rgba(202, 20, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
  padding-top: 2px;
}
</style>
